<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />

  <div v-if="!loading" class="container-fluid my-3">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h4>باركود الطلاب — {{ gradeName }} ({{ students.length }})</h4>
      <div class="d-flex gap-2">
        <router-link :to="`/grades/${gradeSlug}/students/barcodes-print`" class="btn btn-warning">
          <i class="pi pi-print me-1"></i> طباعة الكل
        </router-link>
        <router-link :to="`/grades/${gradeSlug}/students`" class="btn btn-secondary">
          <i class="pi pi-arrow-right me-1"></i> رجوع
        </router-link>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered table-hover align-middle">
        <thead class="table-dark">
          <tr>
            <th style="width:60px">#</th>
            <th>اسم الطالب</th>
            <th style="width:100px" class="text-center">رقم الطالب</th>
            <th class="text-center">الباركود</th>
            <th style="width:120px" class="text-center">تحميل</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(student, index) in students" :key="student.user_id">
            <td>{{ index + 1 }}</td>
            <td>{{ student.name }}</td>
            <td class="text-center fw-bold">{{ student.user_id }}</td>
            <td class="text-center py-2">
              <div
                :ref="(el) => storeBcRef(student.user_id, el)"
                class="d-inline-block"
              >
                <BarcodeLabel
                  :studentId="student.user_id"
                  :barWidth="2"
                  :barHeight="55"
                />
              </div>
            </td>
            <td class="text-center">
              <button class="btn btn-sm btn-outline-primary" @click="downloadBarcode(student)">
                <i class="pi pi-download me-1"></i> تحميل
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import BarcodeLabel from '@/components/global/BarcodeLabel.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: 'StudentsBarcodes',
  components: { Breadcrumb, Loading, BarcodeLabel },

  data() {
    return {
      students: [],
      loading: false,
      bcRefs: {},   // { [userId]: HTMLDivElement } — populated via function refs
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },

    gradeSlug() { return this.$route.params.gradeSlug; },

    grade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          ?? this.gradeStore.allGrades.find(g => g.slug === slug)
          ?? null;
    },

    gradeName() { return this.grade?.name ?? this.gradeSlug ?? ''; },

    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.gradeName, path: `/grades/${this.gradeSlug}/students` },
        { name: 'باركود الطلاب', path: this.$route.fullPath },
      ];
    },
  },

  methods: {
    storeBcRef(userId, el) {
      if (el) this.bcRefs[userId] = el;
      else    delete this.bcRefs[userId];
    },

    downloadBarcode(student) {
      const wrapper = this.bcRefs[student.user_id];
      if (!wrapper) return;
      const svgEl = wrapper.querySelector('svg');
      if (!svgEl) return;

      const w = parseFloat(svgEl.getAttribute('width'))  || 220;
      const h = parseFloat(svgEl.getAttribute('height')) || 90;

      const svgData = new XMLSerializer().serializeToString(svgEl);
      const blob    = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url     = URL.createObjectURL(blob);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width  = w * 2;
        canvas.height = h * 2;
        const ctx = canvas.getContext('2d');
        ctx.scale(2, 2);
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const link = document.createElement('a');
        link.download = `barcode-STU-${student.user_id}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        URL.revokeObjectURL(url);
      };
      img.src = url;
    },

    async fetchStudents() {
      this.loading = true;
      const formData = new FormData();
      formData.append('status', 1);
      formData.append('order_by_name', 1);
      if (this.grade?.id) formData.append('academic_year_id', this.grade.id);

      axios.defaults.headers.common['per_page'] = 100000;
      const res = await axios.post('/all_reservations', formData).catch(() => null);
      const raw = res?.data?.data?.data ?? res?.data?.students ?? [];
      this.students = raw.map(s => ({ user_id: s.user_id ?? s.id, name: s.name }));
      this.loading = false;
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length)    await this.gradeStore.fetchAllGrades();
    await this.fetchStudents();
  },
};
</script>
